// Procedure matcher used by the Collaboration Analytics ROI tab.
//
// Resolves a referral_for free-text into a Procedure row from the procedure
// database. The 2026-05 schema rewrite replaced the legacy single-slug
// `key` column with a comma-separated `matchingKey` phrase list (e.g.
// "Watchman, LAAC, LAA closure"), so this matcher splits on commas and
// substring-tests each phrase. The legacy regex fallback table is kept as
// a safety net in case the import is missing or has a typo.
//
// All comparisons are case-insensitive. Returns the matched procedure
// object, or null when nothing matches. Pure function -- safe to call
// inside a useMemo.

const FALLBACK_PATTERNS = [
  { pattern: /pci|stent|percutaneous coronary/i,             nameRx: /\bPCI\b/i },
  { pattern: /tavr|transcatheter aortic valve/i,              nameRx: /\bTAVR\b/i },
  { pattern: /cabg|coronary.*bypass/i,                        nameRx: /CABG isolated|^CABG/i },
  { pattern: /pacemaker|\bppm\b/i,                            nameRx: /pacemaker/i },
  { pattern: /\bicd\b|implantable cardioverter/i,             nameRx: /ICD implant/i },
  { pattern: /afib.*ablation|atrial fibrillation ablation/i,  nameRx: /AF ablation/i },
  { pattern: /diagnostic.*cath|coronary angiography|\blhc\b/i,nameRx: /angiography|LHC/i },
  { pattern: /mitraclip|mitral.*teer/i,                       nameRx: /Mitral TEER/i },
  { pattern: /triclip|tricuspid.*teer/i,                      nameRx: /Tricuspid TEER/i },
  { pattern: /watchman|laac|laa.*closure|left atrial appendage/i, nameRx: /LAAC|Watchman/i },
  { pattern: /asd.*closure|pfo.*closure/i,                    nameRx: /ASD.*PFO/i },
  { pattern: /pvl.*closure|paravalvular/i,                    nameRx: /paravalvular/i },
  { pattern: /\bsavr\b|surgical aortic valve/i,               nameRx: /SAVR/i },
  { pattern: /mitral.*repair|mitral.*replacement/i,           nameRx: /Mitral valve repair/i },
  { pattern: /heart transplant/i,                             nameRx: /Heart transplant/i },
  { pattern: /\blvad\b|durable vad/i,                         nameRx: /LVAD/i },
  { pattern: /ecmo|\becls\b/i,                                nameRx: /ECMO/i },
  { pattern: /\bcea\b|carotid endarterectomy/i,               nameRx: /Carotid endarterectomy/i },
  { pattern: /tcar|carotid.*stent|\bcas\b/i,                  nameRx: /TCAR|carotid stent/i },
  { pattern: /\bevar\b(?!.*fevar)|endovascular.*aaa/i,        nameRx: /EVAR infrarenal/i },
  { pattern: /fevar|branched.*evar|fenestrated/i,             nameRx: /FEVAR|branched EVAR/i },
  { pattern: /\btevar\b|thoracic endograft/i,                 nameRx: /TEVAR/i },
  { pattern: /open.*aaa|abdominal aortic aneurysm|ruptured aaa/i, nameRx: /Open AAA/i },
  { pattern: /fem.*pop|infrainguinal bypass|tibial bypass/i,  nameRx: /Lower-extremity bypass/i },
  { pattern: /amputation|\baka\b|\bbka\b/i,                   nameRx: /amputation/i },
  { pattern: /aortic root|bentall|ascending aorta|aortic arch/i, nameRx: /Aortic root/i },
];

function splitMatchingKey(raw) {
  if (!raw) return [];
  return String(raw)
    .split(',')
    .map(s => s.trim().toLowerCase())
    .filter(Boolean);
}

export function matchProcedure(referralText, procedures) {
  if (!referralText || !Array.isArray(procedures) || procedures.length === 0) {
    return null;
  }

  const lowerText = String(referralText).toLowerCase();

  // Pass 1: split matchingKey on commas, substring-test each phrase.
  for (const proc of procedures) {
    const phrases = splitMatchingKey(proc.matchingKey);
    for (const phrase of phrases) {
      if (phrase.length >= 2 && lowerText.includes(phrase)) {
        return proc;
      }
    }
  }

  // Pass 2: substring on procedureName (parens stripped) for procedures
  // whose matchingKey is empty or didn't hit.
  for (const proc of procedures) {
    const name = proc.procedureName?.toLowerCase();
    if (!name) continue;
    const cleanName = name.replace(/[()]/g, '').trim();
    if (cleanName && lowerText.includes(cleanName)) {
      return proc;
    }
  }

  // Pass 3: CPT code split-and-search.
  for (const proc of procedures) {
    if (!proc.cptCodes) continue;
    const codes = String(proc.cptCodes)
      .split(/[,;]/)
      .map(c => c.trim())
      .filter(Boolean);
    for (const code of codes) {
      if (code.length >= 3 && lowerText.includes(code.toLowerCase())) {
        return proc;
      }
    }
  }

  // Pass 4: legacy regex safety-net. Each pattern matches a referral text
  // shape (e.g. "PCI", "stent", "percutaneous coronary") and resolves to a
  // procedure whose procedureName matches a corresponding regex. This
  // catches typos / abbreviations not present in matchingKey.
  for (const { pattern, nameRx } of FALLBACK_PATTERNS) {
    if (pattern.test(referralText)) {
      const matched = procedures.find(p => p.procedureName && nameRx.test(p.procedureName));
      if (matched) return matched;
    }
  }

  return null;
}

export default matchProcedure;
